import { useState } from "react";
import { DownloadsHeader } from "@/components/ManualLink";
import { SlidersHorizontal, Sun, CloudFog, Palette, Columns2, Cpu, Film, Bookmark } from "lucide-react";
import { BinaryGrid } from "@/components/BinaryGrid";
import { BeforeAfter } from "@/components/SkinSection";

export const EMBER = "#ff6a3d";

const FEATURES = [
  { icon: SlidersHorizontal, title: "Spatial equalizer", body: "Six frequency bands, from grain to volume: Fine, Detail, Texture, Structure, Modelling and Volume. Soften the digital edge, bring back the modelling of a face, and add presence without sharpening halos." },
  { icon: Sun, title: "Halation", body: "The red-orange glow film produces around bright sources. Threshold, radius, amount, red, orange or custom tint, and saturation." },
  { icon: CloudFog, title: "Diffusion", body: "Pro-Mist and Glimmerglass style: highlights spread into the image, bloom around the brightest points and an optional misty black lift." },
  { icon: Film, title: "Film grain", body: "Organic, animated grain with 35mm, 16mm and Super 8 stocks: amount, size, softness, separate response in shadows, midtones and highlights, and mono, colour or 3D film dye grain: cyan, magenta and yellow dye clouds that melt into the image instead of sitting on top." },
  { icon: Bookmark, title: "My looks", body: "Save your own looks in 5 slots on your machine, or export a look code to copy-paste and share with other colorists." },
  { icon: Palette, title: "Film looks", body: "Organic, 35mm Kodak, 16mm, Pro-Mist 1/4 and Digital clean presets load every module at once. Tweak anything afterwards." },
  { icon: Columns2, title: "Skin aware", body: "Use the automatic skin mask to equalise skin only, or everything but skin. Inspect the mask, the halation layer and the detail change." },
  { icon: Sun, title: "Film compression", body: "Film-style highlight rolloff runs first in the chain: highlights stop clipping and roll off into dense whites. Every film look sets it for you." },
  { icon: Cpu, title: "GPU native · ACES ready", body: "One plugin, three modules you can switch on and off, running inside Resolve's GPU memory (Metal, CUDA or OpenCL). Fully offline. Works in Rec.709, ACEScct, ACEScc, ACEScg, ACES2065-1 and DaVinci Wide Gamut projects." },
];

const DEMOS = [
  { id: "day", tab: "Day · 35mm Kodak", before: "/texture-35mm-before-v5.jpg", after: "/texture-35mm-after-v5.jpg", label: "KolorKlinic Texture · 35mm Kodak" },
  { id: "night", tab: "Warehouse · 16mm", before: "/texture-16mm-before-v5.jpg", after: "/texture-16mm-after-v5.jpg", label: "KolorKlinic Texture · 16mm" },
];

function TextureDemo() {
  const [d, setD] = useState(DEMOS[0]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2" data-testid="texture-demo-tabs">
        {DEMOS.map((x) => (
          <button key={x.id} onClick={() => setD(x)} className="px-3 py-1.5 text-xs font-mono uppercase tracking-wider border transition-colors"
            style={d.id === x.id ? { borderColor: EMBER, color: "#000", background: EMBER } : { borderColor: "#2a2f3d", color: "#9ba3b4" }} data-testid={`texture-demo-tab-${x.id}`}>{x.tab}</button>
        ))}
      </div>
      <BeforeAfter key={d.id} id="texture" before={d.before} after={d.after} color={EMBER} label={d.label} alt={`Processed with KolorKlinic Texture, ${d.tab}`} />
    </div>
  );
}

const GRAINS = [
  { id: "colour-dye", tab: "Colour vs Film dye", before: "/texture-grain-colour-v3.jpg", after: "/texture-grain-dye-v3.jpg", bl: "colour grain", al: "film dye layers" },
  { id: "mono-colour", tab: "Mono vs Colour", before: "/texture-grain-mono-v3.jpg", after: "/texture-grain-colour-v3.jpg", bl: "mono grain", al: "colour grain" },
  { id: "mono-dye", tab: "Mono vs Film dye", before: "/texture-grain-mono-v3.jpg", after: "/texture-grain-dye-v3.jpg", bl: "mono grain", al: "film dye layers" },
];

function GrainDemo() {
  const [d, setD] = useState(GRAINS[0]);
  return (
    <div className="space-y-3" data-testid="texture-grain-compare">
      <div className="flex flex-wrap items-center gap-2" data-testid="texture-grain-tabs">
        <h3 className="font-display uppercase text-xl mr-2">Grain colour</h3>
        {GRAINS.map((x) => (
          <button key={x.id} onClick={() => setD(x)} className="px-3 py-1.5 text-xs font-mono uppercase tracking-wider border transition-colors"
            style={d.id === x.id ? { borderColor: EMBER, color: "#000", background: EMBER } : { borderColor: "#2a2f3d", color: "#9ba3b4" }} data-testid={`texture-grain-tab-${x.id}`}>{x.tab}</button>
        ))}
      </div>
      <BeforeAfter key={d.id} id="texture-grain" before={d.before} after={d.after} color={EMBER} beforeLabel={d.bl} label={d.al} alt={`KolorKlinic Texture, ${d.tab}`} />
      <p className="font-mono text-[11px] text-[#5c6479]">2x zoom, same grain amount and size. Film dye layers is the real deal: like a colour negative, the grain is built from three cyan, magenta and yellow dye clouds, each driven by its own channel's exposure. It melts into skin and the grey walls instead of sitting on top like digital noise. Drag the line to compare.</p>
    </div>
  );
}

export const TextureSection = ({ bins, dl, rebuild }) => (
  <section id="texture" className="scroll-mt-6 card p-6 space-y-6" data-testid="texture-section">
    <div>
      <div className="chip mb-3" style={{ color: EMBER, borderColor: EMBER }}>new · v1.1 · €99 lifetime · Metal · CUDA · OpenCL</div>
      <h2 className="font-display uppercase text-3xl md:text-4xl leading-none">KolorKlinic <span style={{ color: EMBER }}>Texture</span></h2>
      <p className="mt-3 text-sm md:text-base text-[#9ba3b4] max-w-2xl leading-relaxed">
        The organic texture of film for DaVinci Resolve: a spatial equalizer, halation, diffusion and film grain in one OFX.
        It breaks the clinical look of digital sensors and gives back presence and relief. Download it free; the trial adds a small watermark.
      </p>
    </div>
    <TextureDemo />
    <GrainDemo />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {FEATURES.map(({ icon: Icon, title, body }) => (
        <div key={title} className="demo-card bg-[#0e1013] border border-[#2a2f3d] p-4" data-testid={`texture-feature-${title.split(" ")[0].toLowerCase()}`}>
          <div className="flex items-center gap-2 mb-1"><Icon size={16} style={{ color: EMBER }} /><h3 className="font-display uppercase text-base">{title}</h3></div>
          <p className="text-sm text-[#9ba3b4] leading-relaxed">{body}</p>
        </div>
      ))}
    </div>
    <div>
      <DownloadsHeader slug="Texture" name="Texture" color={EMBER} testid="texture-manual-download-btn" />
      <BinaryGrid prefix="texture-" bins={bins} dl={dl} rebuild={rebuild} />
    </div>
  </section>
);
